/**
 * useCategoryManagement Hook
 *
 * Custom hook for managing contractor categories and services
 * Handles category selection, subcategory pricing, and state management
 */

import { useState, useEffect, useMemo } from 'react';
import type { Category, Service } from '../../../api';

export interface CategoryWithSubcategories {
  categoryId: string;
  subcategories: SubcategoryWithPrice[];
}

export interface SubcategoryWithPrice {
  subcategoryId: string;
  price: string;
  unit: string;
}

export interface UseCategoryManagementOptions {
  availableCategories?: Category[];
  myCategories?: any[];
  myServices?: Service[];
  isEnabled: boolean;
}

export function useCategoryManagement({
  availableCategories,
  myCategories,
  myServices,
  isEnabled,
}: UseCategoryManagementOptions) {
  const [selectedCategoryGroups, setSelectedCategoryGroups] = useState<
    CategoryWithSubcategories[]
  >([]);

  // Get root categories (parentId === null)
  const rootCategories = useMemo(() => {
    return availableCategories?.filter((cat) => cat.parentId === null) || [];
  }, [availableCategories]);

  // Get subcategories for a given parent category
  const getSubcategories = (parentId: string): Category[] => {
    return availableCategories?.filter((cat) => cat.parentId === parentId) || [];
  };

  // Load categories and services when they become available
  useEffect(() => {
    if (!isEnabled) return;

    console.log('[useCategoryManagement] Data:', {
      hasMyCategories: !!myCategories,
      hasMyServices: !!myServices,
      hasAvailableCategories: !!availableCategories,
    });

    if (myCategories && myServices && availableCategories) {
      console.log('[useCategoryManagement] Building category groups...');

      const categoryMap = new Map<string, CategoryWithSubcategories>();

      myCategories.forEach((assignedCategory: any) => {
        // Handle both possible API response formats
        const categoryId =
          'categoryId' in assignedCategory
            ? assignedCategory.categoryId
            : assignedCategory.id;

        const fullCategory = availableCategories.find((c) => c.id === categoryId);
        if (!fullCategory) {
          console.warn('[useCategoryManagement] Category not found:', categoryId);
          return;
        }

        // If it's a root category, initialize it
        if (fullCategory.parentId === null) {
          if (!categoryMap.has(fullCategory.id)) {
            categoryMap.set(fullCategory.id, {
              categoryId: fullCategory.id,
              subcategories: [],
            });
          }
        } else {
          // If it's a subcategory, add it to the parent category
          const parentId = fullCategory.parentId;
          if (!categoryMap.has(parentId)) {
            categoryMap.set(parentId, {
              categoryId: parentId,
              subcategories: [],
            });
          }

          const service = myServices.find((s) => s.categoryId === fullCategory.id);

          categoryMap.get(parentId)!.subcategories.push({
            subcategoryId: fullCategory.id,
            price: service?.price?.toString() || '',
            unit: service?.unit || 'per Hour',
          });
        }
      });

      const groups = Array.from(categoryMap.values());
      console.log('[useCategoryManagement] Built groups:', groups.length);
      setSelectedCategoryGroups(groups);
    }
  }, [myCategories, myServices, availableCategories, isEnabled]);

  const handleAddCategoryGroup = () => {
    if (selectedCategoryGroups.length >= 5) {
      alert('You can select maximum 5 categories');
      return;
    }
    setSelectedCategoryGroups([
      ...selectedCategoryGroups,
      {
        categoryId: '',
        subcategories: [],
      },
    ]);
  };

  const handleRemoveCategoryGroup = (index: number) => {
    setSelectedCategoryGroups(selectedCategoryGroups.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (index: number, categoryId: string) => {
    const newGroups = [...selectedCategoryGroups];
    newGroups[index] = {
      categoryId,
      subcategories: [], // Reset subcategories when category changes
    };
    setSelectedCategoryGroups(newGroups);
  };

  const handleSubcategoryToggle = (groupIndex: number, subcategoryId: string) => {
    const newGroups = [...selectedCategoryGroups];
    const group = newGroups[groupIndex];

    const existingIndex = group.subcategories.findIndex(
      (s) => s.subcategoryId === subcategoryId
    );

    if (existingIndex >= 0) {
      // Remove subcategory
      group.subcategories = group.subcategories.filter((_, i) => i !== existingIndex);
    } else {
      // Add subcategory
      group.subcategories.push({
        subcategoryId,
        price: '',
        unit: 'per Hour',
      });
    }

    setSelectedCategoryGroups(newGroups);
  };

  const handleSubcategoryPriceChange = (
    groupIndex: number,
    subcategoryId: string,
    field: 'price' | 'unit',
    value: string
  ) => {
    const newGroups = [...selectedCategoryGroups];
    const subcategory = newGroups[groupIndex].subcategories.find(
      (s) => s.subcategoryId === subcategoryId
    );

    if (subcategory) {
      subcategory[field] = value;
      setSelectedCategoryGroups(newGroups);
    }
  };

  return {
    selectedCategoryGroups,
    rootCategories,
    getSubcategories,
    handleAddCategoryGroup,
    handleRemoveCategoryGroup,
    handleCategoryChange,
    handleSubcategoryToggle,
    handleSubcategoryPriceChange,
  };
}
